import type { HierarcicalObjectReference, ObjectData, ObjectId } from "@novorender/data-js-api";

import { getFilePathFromObjectPath, getParentPath, isLeafNode } from "utils/objectData";
import { getFileExtensionFromPath } from "utils/objectPath";

import type { ObjectDataAccess, SelectionResolutionMode, SelectionTarget } from "./types";

/**
 * The parts of {@link ObjectDataAccess.resolveSelection} that must not vary with which
 * backend a session picked, or the same click selects different objects on two machines
 * looking at the same scene. Expressed over the interface's own primitives, so all three
 * share one copy.
 *
 * Resolving *by node class* is each backend's own — a BIM index climbs, the object DB
 * walks paths.
 */

/** Just the picked object: nothing above it qualifies, or there was nothing to look at. */
export function singleSelectionTarget(
    id: ObjectId,
    object?: ObjectData,
    outcome: SelectionTarget["outcome"] = "legacy",
): SelectionTarget {
    return { mainObjectId: id, mainObject: object, highlightIds: new Uint32Array([id]), outcome };
}

/**
 * The object plus its whole subtree.
 *
 * A leaf is answered without asking: {@link ObjectDataAccess.getDescendantIds} is a
 * request on two of the three backends, and a leaf has nothing below it.
 */
export async function highlightIdsFor(
    access: ObjectDataAccess,
    target: HierarcicalObjectReference,
    signal?: AbortSignal,
): Promise<Uint32Array> {
    if (isLeafNode(target)) {
        return new Uint32Array([target.id]);
    }
    const descendants = await access.getDescendantIds(target, signal);
    // `getDescendantIds` is strict, so the target is prepended rather than merged in.
    const ids = new Uint32Array(descendants.length + 1);
    ids[0] = target.id;
    ids.set(descendants, 1);
    return ids;
}

/**
 * A resolved object and everything it stands for. `mainObject` is filled when `target`
 * came from a metadata read rather than a listing — a narrowing, since `ObjectData`
 * extends `HierarcicalObjectReference`.
 */
export async function selectionTargetFor(
    access: ObjectDataAccess,
    target: HierarcicalObjectReference,
    outcome: SelectionTarget["outcome"],
    signal?: AbortSignal,
): Promise<SelectionTarget> {
    return {
        mainObjectId: target.id,
        mainObject: "properties" in target ? (target as ObjectData) : undefined,
        highlightIds: await highlightIdsFor(access, target, signal),
        outcome,
    };
}

/**
 * Resolution as it behaved before `Procore/NodeClass` existed, and still does for a click
 * whose chain carries none: `last-object` is the picked object, `first-object` its parent
 * plus that parent's subtree — one level up and no further, since without a class there is
 * nothing to say which ancestor is a real object.
 *
 * A root, a direct child of a file, and anything inside an `.rvt` resolve to the picked
 * object instead. RVT's hierarchy is fixed at Category → Family → Type → Instance
 * (BM-7989), so rolling an instance up to its type would select every sibling instance.
 */
export async function resolveSelectionLegacy({
    access,
    clicked,
    mode,
    signal,
}: {
    access: ObjectDataAccess;
    clicked: ObjectData;
    mode: SelectionResolutionMode;
    signal?: AbortSignal;
}): Promise<SelectionTarget> {
    if (mode === "last-object") {
        return singleSelectionTarget(clicked.id, clicked);
    }

    const parentPath = getParentPath(clicked.path);
    const filePath = getFilePathFromObjectPath(parentPath);

    if (!parentPath || filePath === parentPath) {
        return singleSelectionTarget(clicked.id, clicked);
    }

    // `filePath` keeps a federated " (N)" suffix, so the extension is not the last `.`-segment.
    if (filePath && getFileExtensionFromPath(filePath) === "rvt") {
        return singleSelectionTarget(clicked.id, clicked);
    }

    // Sequential: the subtree is keyed by the parent, and whether id or path reaches it is
    // exactly what `getDescendantIds` hides — so it cannot be asked for any earlier.
    const parent = await access.getObjectAtPath(parentPath, signal);
    if (!parent || signal?.aborted) {
        return singleSelectionTarget(clicked.id, clicked);
    }

    return selectionTargetFor(access, parent, "legacy", signal);
}
